import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * /watch/:token — tokenized landing page for SMS-shared demo videos.
 * Branded player, no portal navigation. CTA to book a demo.
 */
export default function Watch() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/video-share/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || `Status ${r.status}`);
        }
        return r.json();
      })
      .then(setData)
      .catch((e) => setErr(e.message || String(e)));
  }, [token]);

  return (
    <div data-testid="watch-page" className="min-h-screen bg-slate-50 flex flex-col">
      <header className="px-6 sm:px-10 py-5 border-b border-slate-200 bg-white">
        <a
          href="https://noderahealth.com"
          className="inline-flex items-center gap-3"
          data-testid="watch-brand-link"
        >
          <svg width="32" height="32" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
            <path d="M32 34 V 11" stroke="#1F4853" strokeWidth="3" strokeLinecap="round"/>
            <path d="M32 34 L 14 52" stroke="#1F4853" strokeWidth="3" strokeLinecap="round"/>
            <path d="M32 34 L 50 52" stroke="#1F4853" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="32" cy="34" r="7" fill="#1F4853"/>
            <circle cx="32" cy="10" r="5" fill="#1F4853"/>
            <circle cx="13" cy="52" r="4.5" fill="#1F4853"/>
            <circle cx="51" cy="52" r="4.5" fill="#1F4853"/>
          </svg>
          <span className="text-lg font-bold tracking-tight text-slate-900">
            Nodera <span className="text-slate-500 font-medium">Health</span>
          </span>
        </a>
      </header>

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto">
          {err ? (
            <div
              data-testid="watch-error"
              className="rounded-2xl bg-white border border-rose-200 p-10 text-center"
            >
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-rose-700 mb-2">
                Link unavailable
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3">
                This share link is no longer active.
              </h1>
              <p className="text-slate-600 text-sm">
                It may have expired or been revoked. Reach out to your Nodera
                Health contact for a fresh link.
              </p>
              <a
                href="https://noderahealth.com/demo"
                className="inline-block mt-6 rounded-full bg-slate-900 text-white px-6 py-3 text-sm font-semibold"
              >
                Visit noderahealth.com
              </a>
            </div>
          ) : !data ? (
            <div className="text-center text-slate-500 text-sm py-20">Loading…</div>
          ) : (
            <>
              <div className="mb-5">
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#1F4853] mb-2">
                  {data.cut_label || "Nodera Health video"}
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
                  {data.recipient_name
                    ? `${data.recipient_name.split(" ")[0]}, here's a quick look.`
                    : "A quick look at Nodera Health."}
                </h1>
                {data.custom_message && (
                  <p
                    data-testid="watch-custom-message"
                    className="mt-4 text-base text-slate-600 leading-relaxed max-w-2xl"
                  >
                    {data.custom_message}
                  </p>
                )}
              </div>

              <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200 bg-slate-950 shadow-2xl">
                <video
                  key={data.video_url}
                  data-testid="watch-video-player"
                  src={`${BACKEND}${data.video_url}`}
                  className="w-full"
                  controls
                  playsInline
                  preload="metadata"
                  style={{ aspectRatio: "16 / 9" }}
                />
              </div>

              <div className="mt-8 grid sm:grid-cols-[1fr_auto] items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
                    Want a real walkthrough?
                  </div>
                  <div className="text-base font-bold text-slate-900">
                    Book a 30-minute demo with a Nodera Health team member.
                  </div>
                </div>
                <a
                  href="https://noderahealth.com/demo"
                  data-testid="watch-book-demo-cta"
                  className="inline-flex items-center justify-center rounded-full bg-[#1F4853] text-white px-6 py-3 text-sm font-bold hover:bg-[#163740]"
                >
                  Book a demo →
                </a>
              </div>

              <p className="text-xs text-slate-400 mt-6 text-center">
                This is a private share link. Forwarded? Forward responsibly.
              </p>
            </>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200 px-6 sm:px-10 py-6 text-xs text-slate-500 text-center bg-white">
        © {new Date().getFullYear()} Nodera Health, Inc. ·{" "}
        <a href="https://noderahealth.com" className="hover:text-slate-700 underline">
          noderahealth.com
        </a>
      </footer>
    </div>
  );
}
